//  * @package         src/components/Dashboard/RightBarComponents
//  * @author          Din
//  * @date            March 2026
//  * @type            Dynamic Question Settings Component
//  * @description -   This component renders the dynamic question configuration panel
//  *                  (Contract dropdown, Service checkboxes, resolved Objects list)
<template>
  <div class="dynamic-question-settings">
    <!-- Contract Selection (Single Select Dropdown) -->
    <div class="edit_box">
      <h4 class="edit_head">Select Contract</h4>
      <select
        class="form-control"
        v-model="selectedContractId"
        @change="onContractChange"
        :disabled="isPublished"
      >
        <option value="">-- Select Contract --</option>
        <optgroup label="Contracts">
          <option
            v-for="contract in contractsList"
            :key="contract.in_contract_id"
            :value="contract.in_contract_id"
          >{{ contract.contract_title || contract.contract_name }}</option>
        </optgroup>
        <optgroup label="Other">
          <option value="0">Objects with no contract</option>
        </optgroup>
      </select>
    </div>

    <!-- Service Filter (Multi-select Checkboxes) -->
    <div class="edit_box" v-if="selectedContractId !== ''">
      <h4 class="edit_head">Filter by Services</h4>
      <div class="service-checkbox-list" v-if="servicesList.length > 0">
        <label
          v-for="service in servicesList"
          :key="service.in_service_id"
          class="service-checkbox-item"
        >
          <input
            type="checkbox"
            :value="service.in_service_id"
            v-model="selectedServiceIds"
            @change="onServiceChange"
            :disabled="isPublished"
          />
          <span class="service-name">{{ service.service_type_name }}</span>
        </label>
        <label class="service-checkbox-item no-service-separator">
          <input
            type="checkbox"
            v-model="includeNoService"
            @change="onServiceChange"
            :disabled="isPublished"
          />
          <span class="service-name">Objects with no service</span>
        </label>
      </div>
      <p v-else-if="!isLoadingServices" class="text-muted small">No services found for this contract.</p>
    </div>

    <!-- Resolved Objects List -->
    <div class="edit_box" v-if="selectedContractId !== ''">
      <h4 class="edit_head">
        Service Objects
        <span v-if="objectsList.length > 0" class="badge badge-secondary ml-1">{{ objectsList.length }}</span>
      </h4>

      <div v-if="isLoadingObjects" class="text-center py-2">
        <i class="fa fa-spinner fa-spin"></i> Loading objects...
      </div>

      <div v-else-if="objectsList.length > 0" class="objects-list">
        <div
          v-for="(obj, index) in objectsList"
          :key="obj.in_object_id"
          class="object-item"
        >
          <span class="object-number">{{ index + 1 }}.</span>
          <span class="object-name">{{ obj.display_name || obj.object_name }}</span>
        </div>
      </div>

      <div v-else class="text-muted small py-2">
        No objects found for the selected filters.
      </div>
    </div>

    <!-- Status Messages -->
    <div v-if="errorMsg" class="alert alert-danger small mt-2">{{ errorMsg }}</div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "DynamicQuestionSettings",
  props: {
    questionMasterId: {
      type: [Number, String],
      default: null
    },
    surveyId: {
      type: [Number, String],
      default: null
    },
    isPublished: {
      type: Boolean,
      default: false
    },
    sessionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      contractsList: [],
      servicesList: [],
      objectsList: [],
      selectedContractId: "",
      selectedServiceIds: [],
      includeNoService: false,
      isLoadingContracts: false,
      isLoadingServices: false,
      isLoadingObjects: false,
      errorMsg: "",
      configLoaded: false
    };
  },
  watch: {
    questionMasterId: {
      handler(newVal) {
        if (newVal) {
          this.resetState();
          this.loadContracts();
          this.loadExistingConfig();
        }
      },
      immediate: true
    }
  },
  methods: {
    resetState() {
      this.selectedContractId = "";
      this.selectedServiceIds = [];
      this.includeNoService = false;
      this.servicesList = [];
      this.objectsList = [];
      this.errorMsg = "";
      this.configLoaded = false;
    },

    getHeaders() {
      return {
        headers: {
          "Content-Type": "application/json",
          Authorization: this.sessionData["jwt_token"]
        }
      };
    },

    getBaseParams() {
      return {
        responseType: "json",
        lang: this.sessionData["lang"],
        request_id: this.sessionData["requestId"],
        in_subscription_id: this.sessionData["subscriptionId"]
      };
    },

    async loadContracts() {
      this.isLoadingContracts = true;
      try {
        let url = process.env.API_PATH + "/getContractsList";
        let response = await axios.post(
          url,
          this.getBaseParams(),
          this.getHeaders()
        );
        if (response.data.flag === 1) {
          this.contractsList = response.data.data || [];
        } else {
          this.errorMsg = response.data.msg || "Failed to load contracts.";
        }
      } catch (e) {
        this.errorMsg = "Error loading contracts.";
        console.error("loadContracts error:", e);
      } finally {
        this.isLoadingContracts = false;
      }
    },

    async loadExistingConfig() {
      if (!this.questionMasterId) return;
      try {
        let url = process.env.API_PATH + "/getDynamicQuestionConfig";
        let response = await axios.post(
          url,
          {
            ...this.getBaseParams(),
            in_question_master_id: this.questionMasterId
          },
          this.getHeaders()
        );
        if (response.data.flag === 1 && response.data.data) {
          let config = response.data.data;
          this.selectedContractId = config.in_contract_id !== null ? config.in_contract_id : "0";
          this.includeNoService = config.in_include_no_service == 1;

          // Load services for the selected contract
          await this.loadServices();

          // Set selected service IDs from saved config
          if (config.services && config.services.length > 0) {
            this.selectedServiceIds = config.services.map(s => s.in_service_id);
          }

          // Load objects (with filters applied)
          await this.loadObjects();

          this.configLoaded = true;
        }
      } catch (e) {
        console.error("loadExistingConfig error:", e);
      }
    },

    async onContractChange() {
      this.selectedServiceIds = [];
      this.includeNoService = false;
      this.servicesList = [];
      this.objectsList = [];
      this.errorMsg = "";

      if (this.selectedContractId !== "") {
        await this.loadServices();
        await this.loadObjects();
      }
      this.$emit("configChanged");
    },

    async onServiceChange() {
      await this.loadObjects();
      this.$emit("configChanged");
    },

    async loadServices() {
      this.isLoadingServices = true;
      try {
        let contractParam = this.selectedContractId === "0" ? null : this.selectedContractId;
        let url = process.env.API_PATH + "/getServicesByContract";
        let response = await axios.post(
          url,
          {
            ...this.getBaseParams(),
            in_contract_id: contractParam
          },
          this.getHeaders()
        );
        if (response.data.flag === 1) {
          this.servicesList = response.data.data || [];
        }
      } catch (e) {
        console.error("loadServices error:", e);
      } finally {
        this.isLoadingServices = false;
      }
    },

    async loadObjects() {
      this.isLoadingObjects = true;
      try {
        let contractParam = this.selectedContractId === "0" ? null : this.selectedContractId;
        let url = process.env.API_PATH + "/getObjectsByContractAndServices";
        let response = await axios.post(
          url,
          {
            ...this.getBaseParams(),
            in_contract_id: contractParam,
            service_ids: this.selectedServiceIds.length > 0 ? this.selectedServiceIds : [],
            in_include_no_service: this.includeNoService ? 1 : 0
          },
          this.getHeaders()
        );
        if (response.data.flag === 1) {
          this.objectsList = response.data.data || [];
        }
      } catch (e) {
        console.error("loadObjects error:", e);
      } finally {
        this.isLoadingObjects = false;
      }
    },

    getDynamicConfigPayload() {
      if (this.selectedContractId === '' || this.objectsList.length === 0) {
        return null;
      }
      let contractParam = this.selectedContractId === "0" ? null : this.selectedContractId;
      return {
        in_contract_id: contractParam,
        service_ids: this.selectedServiceIds,
        in_include_no_service: this.includeNoService ? 1 : 0,
        objects: this.objectsList.map(obj => ({
          in_object_id: obj.in_object_id,
          display_name: obj.display_name || obj.object_name
        }))
      };
    }
  }
};
</script>

<style scoped>
.dynamic-question-settings {
  padding: 0;
}

.dynamic-question-settings .edit_box {
  margin-bottom: 10px;
}

.service-checkbox-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  background: #fff;
  border-radius: 5px;
}

.service-checkbox-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
  cursor: pointer;
  margin-bottom: 0;
}

.service-checkbox-item input[type="checkbox"] {
  margin-right: 8px;
  flex-shrink: 0;
}

.no-service-separator {
  border-top: 1px solid #e0e0e0;
  margin-top: 6px;
  padding-top: 8px !important;
}

.service-name {
  font-size: 13px;
  color: #333;
}

.objects-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: #fff;
  border-radius: 5px;
}

.object-item {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.object-number {
  flex: 0 0 20px;
  font-size: 12px;
  color: #999;
}

.object-name {
  font-size: 13px;
  color: #333;
}

.badge-secondary {
  background-color: #6c757d;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/RightBarComponents/DynamicQuestionSettings.vue